import React, { useRef, useState, useEffect } from 'react';
import { FileText, Maximize2, Minimize2 } from 'lucide-react';
import './ViewerComponents.css';

interface ScormViewerProps {
  contentUrl: string;
  title: string;
}

export const ScormViewer: React.FC<ScormViewerProps> = ({ contentUrl, title }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Initialize SCORM APIs on mount
  useEffect(() => {
    // SCORM 1.2 API Implementation
    function SCORMAPI(this: any) {
      this.data = {};
      this.initialized = false;
      this.terminated = false;

      this.LMSInitialize = function(param: string) {
        console.log('SCORM API: LMSInitialize called');
        this.initialized = true;
        this.terminated = false;
        return 'true';
      };

      this.LMSFinish = function(param: string) {
        console.log('SCORM API: LMSFinish called');
        this.terminated = true;
        return 'true';
      };

      this.LMSGetValue = function(element: string) {
        console.log('SCORM API: LMSGetValue', element);
        if (element === 'cmi.core.lesson_status') {
          return this.data[element] || 'not attempted';
        }
        if (element === 'cmi.core.student_id') return '1';
        if (element === 'cmi.core.student_name') return 'Learner';
        if (element === 'cmi.core.lesson_location') return this.data[element] || '';
        if (element === 'cmi.suspend_data') return this.data[element] || '';
        return this.data[element] || '';
      };

      this.LMSSetValue = function(element: string, value: string) {
        console.log('SCORM API: LMSSetValue', element, '=', value);
        this.data[element] = value;
        if (element === 'cmi.core.lesson_status') {
          console.log('SCORM: Lesson status =', value);
        }
        if (element === 'cmi.core.score.raw') {
          console.log('SCORM: Score =', value);
        }
        return 'true';
      };

      this.LMSCommit = function(param: string) {
        console.log('SCORM API: LMSCommit - Data saved');
        return 'true';
      };

      this.LMSGetLastError = function() {
        return '0';
      };

      this.LMSGetErrorString = function(errorCode: string) {
        return 'No error';
      };

      this.LMSGetDiagnostic = function(errorCode: string) {
        return 'No error';
      };
    }

    // SCORM 2004 API Implementation
    function SCORMAPI_1484_11(this: any) {
      this.data = {};
      this.initialized = false;
      this.terminated = false;

      this.Initialize = function(param: string) {
        console.log('SCORM 2004 API: Initialize called');
        this.initialized = true;
        this.terminated = false;
        return 'true';
      };

      this.Terminate = function(param: string) {
        console.log('SCORM 2004 API: Terminate called');
        this.terminated = true;
        return 'true';
      };

      this.GetValue = function(element: string) {
        console.log('SCORM 2004 API: GetValue', element);
        if (element === 'cmi.completion_status') {
          return this.data[element] || 'incomplete';
        }
        if (element === 'cmi.learner_id') return '1';
        if (element === 'cmi.learner_name') return 'Learner';
        if (element === 'cmi.location') return this.data[element] || '';
        if (element === 'cmi.suspend_data') return this.data[element] || '';
        return this.data[element] || '';
      };

      this.SetValue = function(element: string, value: string) {
        console.log('SCORM 2004 API: SetValue', element, '=', value);
        this.data[element] = value;
        if (element === 'cmi.completion_status') {
          console.log('SCORM 2004: Completion status =', value);
        }
        if (element === 'cmi.score.raw') {
          console.log('SCORM 2004: Score =', value);
        }
        return 'true';
      };

      this.Commit = function(param: string) {
        console.log('SCORM 2004 API: Commit - Data saved');
        return 'true';
      };

      this.GetLastError = function() {
        return '0';
      };

      this.GetErrorString = function(errorCode: string) {
        return 'No error';
      };

      this.GetDiagnostic = function(errorCode: string) {
        return 'No error';
      };
    }

    // Initialize APIs on window
    (window as any).API = new (SCORMAPI as any)();
    (window as any).API_1484_11 = new (SCORMAPI_1484_11 as any)();

    // Add IsLmsPresent function - required by some SCORM packages to detect LMS
    (window as any).IsLmsPresent = function() {
      return true;
    };

    console.log('✅ SCORM APIs initialized successfully');
    console.log('SCORM 1.2 API available as window.API');
    console.log('SCORM 2004 API available as window.API_1484_11');
    console.log('IsLmsPresent function available');

    // Cleanup function
    return () => {
      delete (window as any).API;
      delete (window as any).API_1484_11;
      delete (window as any).IsLmsPresent;
      console.log('SCORM APIs cleaned up');
    };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement && iframeRef.current?.parentElement) {
      iframeRef.current.parentElement.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  return (
    <div className="s-admin-content-viewer content-viewer--scorm">
      <div className="s-admin-content-viewer__header">
        <div className="s-admin-content-viewer__header-left">
          <FileText size={20} className="s-admin-content-viewer__icon" />
          <span className="s-admin-content-viewer__title">SCORM Package</span>
        </div>
        <button
          onClick={toggleFullscreen}
          className="s-admin-content-viewer__btn content-viewer__btn--secondary"
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        </button>
      </div>
      <div className="s-admin-content-viewer__body">
        <iframe
          ref={iframeRef}
          src={contentUrl}
          className="s-admin-content-viewer__iframe"
          title={title}
          allow="autoplay; fullscreen; accelerometer; gyroscope; picture-in-picture; encrypted-media"
          allowFullScreen
        />
      </div>
    </div>
  );
};
